import React, { useState, useEffect } from "react";
import {
  BellIcon as Bell,
  HeartIcon as Heart,
  MapPinIcon as MapPin,
  UserIcon as User,
} from "@heroicons/react/24/outline";
import { CheckIcon, XIcon, Loader2, Share } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Notification, NotificationType } from "@/types/notifications";
import { formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router-dom";
import CustomDropdown from "@/components/ui/CustomDropdown";
import { cn } from "@/lib/utils";
import userService from "@/services/userService";
import { toast } from "@/hooks/use-toast";
import useNotificationsStore from "@/stores/notifications/notificationsStore";
import useAuthStore from "@/stores/user/authStore";

const NotificationsDropdown: React.FC = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuthStore();

  const {
    notifications,
    unreadCount,
    isLoading,
    error,
    markAsRead,
    updateNotifications,
    fetchNotifications,
    setupSubscription,
  } = useNotificationsStore();

  useEffect(() => {
    if (!user?.user_id) return;

    // Initial fetch
    fetchNotifications(user.user_id);

    // Setup realtime subscription
    const cleanup = setupSubscription(user.user_id);

    return () => {
      cleanup();
    };
  }, [user?.user_id]);

  // Mark notifications as read when dropdown is opened
  useEffect(() => {
    if (isOpen && notifications.length > 0) {
      const unreadIds = notifications
        .filter((notification) => !notification.is_read)
        .map((notification) => notification.id);

      if (unreadIds.length > 0) {
        markAsRead(unreadIds);
      }
    }
  }, [isOpen, notifications, markAsRead]);

  const getNotificationStyles = (type: string) => {
    const baseIconClass = "h-4 w-4";
    switch (type) {
      case "new_follower":
        return {
          icon: <User className={cn(baseIconClass, "text-indigo-600")} />,
          bgColor: "bg-indigo-50",
          ringColor: "ring-indigo-600/20",
          textColor: "text-indigo-600",
        };
      case "follow_request":
        return {
          icon: <User className={cn(baseIconClass, "text-violet-600")} />,
          bgColor: "bg-violet-50",
          ringColor: "ring-violet-600/20",
          textColor: "text-violet-600",
        };
      case "trip_invited":
        return {
          icon: <MapPin className={cn(baseIconClass, "text-blue-600")} />,
          bgColor: "bg-blue-50",
          ringColor: "ring-blue-600/20",
          textColor: "text-blue-600",
        };
      case "trip_removed":
        return {
          icon: <MapPin className={cn(baseIconClass, "text-rose-600")} />,
          bgColor: "bg-rose-50",
          ringColor: "ring-rose-600/20",
          textColor: "text-rose-600",
        };
      case "trip_liked":
        return {
          icon: <Heart className={cn(baseIconClass, "text-red-600")} />,
          bgColor: "bg-red-50",
          ringColor: "ring-red-600/20",
          textColor: "text-red-600",
        };
      case "trip_shared":
        return {
          icon: <Share className={cn(baseIconClass, "text-green-600")} />,
          bgColor: "bg-green-50",
          ringColor: "ring-green-600/20",
          textColor: "text-green-600",
        };
      default:
        return {
          icon: <Bell className={cn(baseIconClass, "text-neutral-600")} />,
          bgColor: "bg-neutral-50",
          ringColor: "ring-neutral-600/20",
          textColor: "text-neutral-600",
        };
    }
  };

  const handleApproveRequest = async (
    e: React.MouseEvent,
    notification: Notification
  ) => {
    e.stopPropagation();
    try {
      await userService.approveFollowRequest(notification.actor_id, "approve");
      const updatedNotifications = notifications.map((n) =>
        n.id === notification.id
          ? { ...n, type: "new_follower" as NotificationType }
          : n
      );
      updateNotifications(updatedNotifications as Notification[]);
      toast({
        title: "Follow request approved",
        description: "You are now following " + notification.actor_name,
      });
    } catch (error) {
      console.error("Error approving follow request:", error);
      toast({
        title: "Error",
        description: "Failed to approve follow request",
        variant: "destructive",
      });
    }
  };

  const handleDeclineRequest = async (
    e: React.MouseEvent,
    notification: Notification
  ) => {
    e.stopPropagation();
    try {
      await userService.approveFollowRequest(notification.actor_id, "decline");
      const updatedNotifications = notifications.filter(
        (n) => n.id !== notification.id
      );
      updateNotifications(updatedNotifications);
      toast({
        title: "Follow request declined",
        description:
          "Request from " + notification.actor_name + " was declined",
      });
    } catch (error) {
      console.error("Error declining follow request:", error);
      toast({
        title: "Error",
        description: "Failed to decline follow request",
        variant: "destructive",
      });
    }
  };

  const renderNotificationMessage = (
    notification: Notification,
    defaultClass: string = "text-sm text-neutral-900 font-normal"
  ): React.ReactNode => {
    const styles = getNotificationStyles(notification.type);

    switch (notification.type) {
      case "new_follower":
        return (
          <p className={defaultClass}>
            <span className="font-medium">{notification.actor_name}</span>{" "}
            started following you
          </p>
        );
      case "follow_request":
        return (
          <div className="flex flex-col gap-2">
            <p className={defaultClass}>
              <span className="font-medium">{notification.actor_name}</span>{" "}
              requested to follow you
            </p>
            <div className="flex gap-2">
              <button
                className="flex flex-row items-center p-2 ring-1 rounded-md py-1 ring-neutral-600/20 text-xs hover:bg-emerald-50 hover:text-emerald-600 hover:ring-emerald-600/20 transition-colors"
                onClick={(e) => handleApproveRequest(e, notification)}
              >
                <CheckIcon className="h-3 w-3 mr-1.5" />
                Accept
              </button>
              <button
                className="flex flex-row items-center p-2 ring-1 rounded-md ring-neutral-600/20 text-xs hover:bg-rose-50 hover:text-rose-600 hover:ring-rose-600/20 transition-colors"
                onClick={(e) => handleDeclineRequest(e, notification)}
              >
                <XIcon className="h-3 w-3 mr-1.5" />
                Decline
              </button>
            </div>
          </div>
        );
      case "trip_invited":
        return (
          <p className={defaultClass}>
            <span className="font-medium">{notification.actor_name}</span> added
            you to{" "}
            <span className={styles.textColor}>
              {notification.metadata.trip_name}
            </span>
          </p>
        );
      case "trip_removed":
        return (
          <p className={defaultClass}>
            You were removed from{" "}
            <span className={styles.textColor}>{notification.trip_name}</span>
          </p>
        );
      case "trip_shared":
        return (
          <p className={defaultClass}>
            <span className="font-medium">{notification.actor_name}</span>{" "}
            shared{" "}
            <span className={styles.textColor}>
              {notification.metadata.trip_name}
            </span>
          </p>
        );
      case "trip_liked":
        return (
          <p className={defaultClass}>
            <span className="font-medium">{notification.actor_name}</span> liked{" "}
            <span className={styles.textColor}>{notification.trip_name}</span>
          </p>
        );
      default:
        return <p className={defaultClass}>New notification</p>;
    }
  };

  const handleSingleNotificationClick = async (notification: Notification) => {
    if (!notification.is_read) {
      try {
        await markAsRead([notification.id]);
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
      }
    }

    if (notification.metadata.action === "added_to_trip") {
      navigate(`/trip-builder/${notification.trip_id}`);
    } else if (notification.type === "new_follower") {
      navigate(`/account/${notification.actor_id}`);
    } else if (notification.metadata.action === "liked_trip") {
      navigate(`/trip-builder/${notification.trip_id}`);
    } else if (notification.type === "trip_invited") {
      navigate(`/trip-builder/${notification.metadata.trip_id}`);
    }
  };

  if (isLoading) {
    return (
      <button className="relative h-10 w-10 flex items-center justify-center bg-white/90 backdrop-blur-sm border border-neutral-200 rounded-full hover:bg-neutral-50 transition-colors duration-200 shadow-sm focus:outline-none">
        <Bell className="h-5 w-5 text-neutral-700" />
        <span className="w-5 h-5 absolute -top-2 -right-2 bg-indigo-600 text-white text-xs font-medium rounded-full flex items-center justify-center">
          <Loader2 className="h-3 w-3 animate-spin" />
        </span>
      </button>
    );
  }

  return (
    <>
      <CustomDropdown
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        trigger={
          <button className="relative h-10 w-10 2xl:h-11 2xl:w-11 flex items-center justify-center bg-white/90 backdrop-blur-sm border border-neutral-200 rounded-full hover:bg-neutral-50 transition-colors duration-200 shadow-sm focus:outline-none">
            <Bell className="h-5 w-5 2xl:h-[22px] 2xl:w-[22px] text-neutral-700" />
            {unreadCount > 0 && (
              <span className="w-5 h-5 absolute -top-2 -right-2 bg-indigo-600 text-white text-[10px] font-medium rounded-full flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </button>
        }
        className="w-96 h-[30vh] overflow-y-auto no-scrollbar"
        title="Notifications"
      >
        {error && (
          <div className="flex flex-col h-full justify-center items-center text-center text-neutral-500">
            <Bell className="h-8 w-8 text-rose-400 mx-auto mb-3" />
            <p className="font-medium text-rose-600">
              Error fetching notifications
            </p>
            <p className="text-xs mt-1 font-light">
              Please try refreshing the page
            </p>
          </div>
        )}

        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={cn(
              "p-4 cursor-pointer transition-all duration-200",
              !notification.is_read
                ? "bg-neutral-50"
                : `hover:bg-neutral-50/50`,
              "border-b border-neutral-100 last:border-b-0"
            )}
            onClick={(e) => {
              e.stopPropagation();
              handleSingleNotificationClick(notification);
            }}
          >
            <div className="flex gap-4 w-full items-start">
              <div
                className={cn(
                  "w-8 h-8 shrink-0 rounded-full flex items-center justify-center ring-1",
                  getNotificationStyles(notification.type).bgColor,
                  getNotificationStyles(notification.type).ringColor
                )}
              >
                {getNotificationStyles(notification.type).icon}
              </div>
              <div className="flex-grow min-w-0">
                {renderNotificationMessage(notification)}
                <p className="text-xs text-neutral-500 mt-2 font-light">
                  {formatDistanceToNow(new Date(notification.created_at), {
                    addSuffix: true,
                  })}
                </p>
              </div>
              {!notification.is_read && (
                <div className="w-2 h-2 rounded-full bg-indigo-600 shrink-0" />
              )}
            </div>
          </div>
        ))}

        {!isLoading && notifications.length === 0 && !error && (
          <div className="flex flex-col h-full justify-center items-center text-center text-neutral-500">
            <Bell className="h-8 w-8 text-neutral-400 mx-auto mb-3" />
            <p className="font-medium text-neutral-600">No new notifications</p>
            <p className="text-xs mt-1 font-light">
              We'll notify you when something happens
            </p>
          </div>
        )}
      </CustomDropdown>
    </>
  );
};

export default NotificationsDropdown;
